// Overlap Detection Utility
// Detects scheduling conflicts for the Class Manager feature
// This is a CLIENT-SIDE warning only - does not block submission

import { parseISO, isValid, isBefore, isAfter, isEqual, parse } from "date-fns";

/**
 * Parse various date formats into a Date object
 * @param {Date|string|number} value
 * @returns {Date|null}
 */
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === "string") {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }
  return null;
};

/**
 * Extract start and end times from an event object
 * Supports multiple key naming conventions
 * @param {Object} event
 * @returns {{ start: Date|null, end: Date|null }}
 */
const getEventTimes = (event) => {
  if (!event || typeof event !== "object") return { start: null, end: null };

  const startRaw =
    event.start ??
    event.startTime ??
    event.start_time ??
    event.begin ??
    event.beginTime;
  const endRaw =
    event.end ??
    event.endTime ??
    event.end_time ??
    event.finish ??
    event.finishTime;

  return {
    start: parseDate(startRaw),
    end: parseDate(endRaw),
  };
};

/**
 * Check if two time ranges overlap
 * Two events overlap if: startA < endB AND startB < endA
 * @param {Date} startA
 * @param {Date} endA
 * @param {Date} startB
 * @param {Date} endB
 * @returns {boolean}
 */
const doRangesOverlap = (startA, endA, startB, endB) => {
  if (!startA || !endA || !startB || !endB) return false;
  // Overlap exists if: startA < endB AND startB < endA
  return isBefore(startA, endB) && isBefore(startB, endA);
};

/**
 * Check if a new event overlaps with any existing events
 * @param {Object} newEvent - The proposed new event { start, end } or { startTime, endTime }
 * @param {Array<Object>} existingEvents - Array of existing events
 * @returns {{ hasOverlap: boolean, overlappingEvents: Array<Object> }}
 */
export function checkEventOverlap(newEvent, existingEvents = []) {
  if (!newEvent || !Array.isArray(existingEvents)) {
    return { hasOverlap: false, overlappingEvents: [] };
  }

  const { start: newStart, end: newEnd } = getEventTimes(newEvent);

  if (!newStart || !newEnd) {
    return { hasOverlap: false, overlappingEvents: [] };
  }

  const overlappingEvents = existingEvents.filter((existing) => {
    const { start: existStart, end: existEnd } = getEventTimes(existing);
    return doRangesOverlap(newStart, newEnd, existStart, existEnd);
  });

  return {
    hasOverlap: overlappingEvents.length > 0,
    overlappingEvents,
  };
}

/**
 * Check overlap given start time string and duration
 * Convenience function for AddClassPage which uses duration instead of end time
 *
 * IMPORTANT: This function handles timezone-aware comparison.
 * - Input startTime and date are treated as LOCAL time (user's timezone)
 * - Existing events from DB may have UTC timestamps (ending in 'Z')
 * - Both are compared using JavaScript Date objects which normalize to UTC internally
 *
 * @param {string} startTime - "HH:mm" format (local time)
 * @param {number} durationMinutes
 * @param {string} date - "YYYY-MM-DD" format
 * @param {Array<Object>} existingEvents - Events from database (may be UTC)
 * @returns {{ hasOverlap: boolean, overlappingEvents: Array<Object> }}
 */
export function checkOverlapWithDuration(
  startTime,
  durationMinutes,
  date,
  existingEvents = []
) {
  if (!startTime || !durationMinutes || !date) {
    return { hasOverlap: false, overlappingEvents: [] };
  }

  try {
    // Parse the local time input properly
    // Using parse() instead of parseISO() to handle "HH:mm" format correctly
    // This creates a Date object in LOCAL timezone
    const [hours, minutes] = startTime.split(":").map(Number);
    const [year, month, day] = date.split("-").map(Number);

    // Create date in local timezone
    const startDateTime = new Date(year, month - 1, day, hours, minutes, 0);

    if (!isValid(startDateTime)) {
      console.warn("Invalid start datetime created:", { date, startTime });
      return { hasOverlap: false, overlappingEvents: [] };
    }

    const endDateTime = new Date(
      startDateTime.getTime() + durationMinutes * 60 * 1000
    );

    // Debug logging (can be removed in production)
    console.log("Overlap check - New event:", {
      startLocal: startDateTime.toLocaleString(),
      endLocal: endDateTime.toLocaleString(),
      startUTC: startDateTime.toISOString(),
      endUTC: endDateTime.toISOString(),
    });

    const newEvent = {
      start: startDateTime,
      end: endDateTime,
    };

    // Filter existing events to only those on the same calendar day
    // This optimization prevents comparing against events on different days
    const sameDayEvents = existingEvents.filter((existing) => {
      const { start: existStart } = getEventTimes(existing);
      if (!existStart) {
        console.log("No start time for event:", existing);
        return false;
      }

      // Check if same calendar day in local time
      // Compare ISO date strings (YYYY-MM-DD) to be timezone-agnostic
      const targetDateStr = `${year}-${String(month).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;

      // Parse ISO string manually to get date part if possible, otherwise rely on local date
      let existDateStr;

      // If we have access to the raw string and it matches ISO format, use it directly
      if (
        existing.start &&
        typeof existing.start === "string" &&
        existing.start.includes("T")
      ) {
        existDateStr = existing.start.split("T")[0];
      } else {
        // Fallback to local date object
        const y = existStart.getFullYear();
        const m = String(existStart.getMonth() + 1).padStart(2, "0");
        const d = String(existStart.getDate()).padStart(2, "0");
        existDateStr = `${y}-${m}-${d}`;
      }

      const isSameDay = targetDateStr === existDateStr;

      if (!isSameDay) {
        // Debugging why it failed
        // console.log(`Date mismatch for event on ${date}:`, {
        //   eventStart: existStart.toLocaleString(),
        //   targetYear: year,
        //   targetMonth: month - 1,
        //   targetDay: day,
        //   eventYear: existStart.getFullYear(),
        //   eventMonth: existStart.getMonth(),
        //   eventDay: existStart.getDate(),
        // });
      }

      return isSameDay;
    });

    // Detailed debug logging if no events found but we expect some
    if (sameDayEvents.length === 0 && existingEvents.length > 0) {
      console.log(
        `[Overlap Debug] Found 0 events for ${date} (Target: ${year}-${month}-${day}). Checking first 3 events to see why:`
      );
      existingEvents.slice(0, 3).forEach((ev, i) => {
        const { start } = getEventTimes(ev);
        if (start) {
          console.log(`Event ${i}:`, {
            original: ev.start,
            parsed: start.toLocaleString(),
            year: start.getFullYear(),
            month: start.getMonth() + 1,
            day: start.getDate(),
            match:
              start.getFullYear() === year &&
              start.getMonth() === month - 1 &&
              start.getDate() === day,
          });
        }
      });
    }

    console.log(
      `Checking ${sameDayEvents.length} events on ${date} for overlap`
    );

    return checkEventOverlap(newEvent, sameDayEvents);
  } catch (error) {
    console.error("Error checking overlap:", error);
    return { hasOverlap: false, overlappingEvents: [] };
  }
}

/**
 * Format overlapping events for display in warning message
 * @param {Array<Object>} events
 * @returns {string}
 */
export function formatOverlapWarning(events) {
  if (!events || events.length === 0) return "";

  const eventDescriptions = events.map((event) => {
    const { start, end } = getEventTimes(event);
    const title = event.title || event.classType || event.class_type || "Class";

    if (start && end) {
      const startStr = start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      const endStr = end.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      return `${title} (${startStr} - ${endStr})`;
    }
    return title;
  });

  if (eventDescriptions.length === 1) {
    return `This time overlaps with: ${eventDescriptions[0]}`;
  }

  return `This time overlaps with ${
    eventDescriptions.length
  } classes:\n• ${eventDescriptions.join("\n• ")}`;
}

/**
 * Format recurring overlap warning with days
 * @param {Array<Object>} events
 * @returns {string}
 */
export function formatRecurringOverlapWarning(events) {
  if (!events || events.length === 0) return "";

  const eventDescriptions = events.map((event) => {
    const { start, end } = getEventTimes(event);
    const title = event.title || event.classType || event.class_type || "Class";
    const day = event.dayOfWeek || "";

    if (start && end) {
      const startStr = start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      const endStr = end.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      return `${day ? day + ": " : ""}${title} (${startStr} - ${endStr})`;
    }
    return `${day ? day + ": " : ""}${title}`;
  });

  return `Conflicts detected on ${
    eventDescriptions.length
  } days:\n• ${eventDescriptions.join("\n• ")}`;
}

/**
 * Check if events are back-to-back (no overlap but no gap)
 * Useful for identifying tight schedules
 * @param {Object} eventA
 * @param {Object} eventB
 * @returns {boolean}
 */
export function areEventsBackToBack(eventA, eventB) {
  const { end: endA } = getEventTimes(eventA);
  const { start: startB } = getEventTimes(eventB);
  const { end: endB } = getEventTimes(eventB);
  const { start: startA } = getEventTimes(eventA);

  if (!endA || !startB || !endB || !startA) return false;

  // A ends exactly when B starts, or B ends exactly when A starts
  return isEqual(endA, startB) || isEqual(endB, startA);
}
